import React, { Component } from "react";
import _, { cloneDeep, isFunction, map, remove } from "lodash";
import { Row, Col, Table, Dropdown, Menu, Checkbox, Tooltip, Divider, Typography, Switch } from "antd";
import { BASE_GUTTER, BASE_MARGIN, FULL_MENU_WIDTH, MIN_SCREEN_WIDTH } from "constants/Layout";
import { DownIcon, LoadingIcon, RightIcon, ResetFilterIcon, SelectColumnIcon } from "./Icons";
import { withRouter } from "react-router-dom";
import { withUserPreferencesContext } from "contexts/UserPreferencesContext";
import { getDataIndex, setInitialDataAndGetOptionalColumns, pageSizeOptions, MakeDNDMenu } from "utils/tableUtils";
import FilterBar from "./FilterBar";
import { getTemporarySessionStorage, setTemporarySessionStorage } from "utils/sessionFilterStoreUtil"
import { withGlobalFilterContext } from "contexts/GlobalFilterContext";
import classNames from "classnames";

/**
 * @description use TableContainerEvolved for server side pagination
 */
class TableContainer extends Component{
    static defaultProps = {
        columns: [],
        extraFilter: [],
        paginationPosition: "bottomRight",
        onColumnShowHide: _.noop,

    }
    static getDerivedStateFromProps(props, state){
        const query = props?.location?.search;
        if(query){
            const param = new URLSearchParams(query);
            const globalSearch = param.get("query");
            if(param.has("query") && globalSearch !== state.globalSearch){
                return {
                    searchText: globalSearch,
                    globalSearch
                }
            }
        }
        return null;
    }
    constructor(props){
        super(props);

        const { tableKey, userPreferencesContext, defaultPageSize } = props;

        // This will set the initial column view
        const { optionalColumns } = setInitialDataAndGetOptionalColumns(userPreferencesContext, tableKey, props.columns);
        
        const orderedOptionalColumns = userPreferencesContext.getColumnOrder(tableKey, optionalColumns);

        const sessionStorageData = tableKey ? (getTemporarySessionStorage(tableKey) || {}) : {};
        let pageOptions = pageSizeOptions;
        if(defaultPageSize && pageOptions.indexOf(defaultPageSize) === -1){
           pageOptions = [...pageOptions, defaultPageSize].sort((a, b) => a - b);
        }
        this.pageSizeOptions = pageOptions;
        const tablePreferences = tableKey ? userPreferencesContext.getTablePreferences(tableKey) : {};
        const pageSize = tablePreferences.pageSize || props.defaultPageSize || 10; 

        this.state = {
            searchText: props.defaultSearch || "",
            page: 1,
            optionalColumns: orderedOptionalColumns,
            filteredInfo: _.isEmpty(sessionStorageData) ? _.merge({}, props.defaultFilter, props.extraFilteredInfo) : sessionStorageData.filters,
            sortedInfo:props.defaultSort || {},
            globalSearch: "",
            pageSize,
            expandedValue: []
        }

        _.bindAll(
            this, 
            "onSearch",
            "onChangeTable",
            "onExternalPaginationChange",
            "updateColumns",
            "resetFilters",
            "handleSwitch",
            "onExpandRow",
            "onDragEnd",
            "onFilterBarChange",
        )
    }
    componentDidMount(){
        if(typeof this.props.tableRef === "function"){
            this.props.tableRef({
                resetFilters: () => {
                    this.resetFilters();
                },
                getActiveSource: () => {
                    return this.getActiveSource();
                },
                getOptionalColumns: () => {
                    const activeOptionalColumns = this.props.userPreferencesContext.getActiveOptionalColumns(this.props.tableKey);
                    return _.map(this.state.optionalColumns, (col) => {
                        return {
                            name: col.value,
                            visible: _.some(activeOptionalColumns, (activeCol) => activeCol === col.value),
                        };
                    });
                },
            });
        }
        // This is need to send the initial state to the parent
        this.props.onColumnShowHide();
    }

    // Since we are Changing the filters from outside and we don't have control on table onChange that why we are invoking setting the state.
    componentDidUpdate(prevProps){
        const { filters: prevFilters, globalFilterContext: { globalFilterHash: prevGlobalFilterHash }, expandedValues: previousExpandedValues } = prevProps
        const { filters, globalFilterContext: { globalFilterHash }, expandedValues } = this.props;
        if(prevFilters !== filters){
            this.saveFiltersInSession(filters);
            this.setState({filteredInfo:filters});
        }
        if (previousExpandedValues !== expandedValues) {
            this.setState({expandedValue: expandedValues});
        }
        if (!_.isEqual(prevGlobalFilterHash, globalFilterHash)) {
            this.resetFilters();
        }
    }

    resetFilters(){
        this.setState({
            filteredInfo: {},
            sortedInfo: {},
            page: 1,
            pageSize: this.props.defaultPageSize || 10
        });
        this.saveFiltersInSession({});
        if(typeof this.props.onChange === 'function'){
            this.props.onChange({},{},{}, {action: "filter"});
        }
    }

    getActiveSource() {
        const { dataSource, columns, extraFilter } = this.props;
        const { filteredInfo } = this.state;

        const allColumns = [...columns, ...extraFilter];
    
        let filterData = dataSource;
        _.forEach(filteredInfo, (value, key) => {
            const currColumn = _.find(allColumns, col => col.dataIndex === key);
            filterData = _.filter(filterData, record => {
                return _.some(value, val => {
                    return currColumn.onFilter(val, record);
                });
            });
        });

        return {
            filterData
        };
    }

    onSearch(e){
        this.setState({
            searchText: e?.target?.value || ""
        });
    }
    onChangeTable(pagination, filters, sorter, extra){
        const state = {};
        const page = _.result(pagination, "current", this.state.page);
        const pageSize = _.result(pagination,"pageSize", this.state.pageSize);
        state.pageSize = pageSize;
        state.page = page;

        if(this.props.tableKey) {
            this.props.userPreferencesContext.updateTablePreferences({ tableKey: this.props.tableKey, preferenceKey: 'pageSize', data: state.pageSize });
        }

        //ENG-36427: filtering out null values because when the table returns filter with null values,
        //advance filter is shown unnecessarily
        //encountered this bug in repo discovery page 
        state.filteredInfo = _.omitBy(filters, _.isNull);
        if (sorter) {
            state.sortedInfo = {column: getDataIndex(sorter.column), order: sorter.order}
        }
        this.setState(state);
        this.saveFiltersInSession(filters);
        if(typeof this.props.onChange === 'function'){
            this.props.onChange(pagination, filters, sorter, extra);
        }
    }
    onExternalPaginationChange(current){
        this.setState({
            page: current
        });
    }
    updateColumns(e){
        const { userPreferencesContext, tableKey } = this.props;
        const filteredInfo = _.clone(this.state.filteredInfo);
        const activeOptionalColumns = userPreferencesContext.getActiveOptionalColumns(tableKey);
        const dataIndex = e.target["data-index"];
        const index = activeOptionalColumns.indexOf(dataIndex);
        if(index > -1){
            if (!this.props.showAdvanceFilter) {
                delete filteredInfo[dataIndex];
            } 
        }
        userPreferencesContext.updateColumnPreference(e.target.checked, dataIndex, tableKey);
        this.setState({
            filteredInfo
        }, () => this.props.onColumnShowHide());
    }

    onDragEnd(result) {
        const { source, destination } = result;
        const { tableKey, userPreferencesContext } = this.props;
        userPreferencesContext.changeColumnSequence(tableKey, source, destination);
    }

    renderColumnOptions(){
        const {optionalColumns} = this.state;
        const { columnsReorder = false } = this.props;
        if(_.isEmpty(optionalColumns)){
            return null;
        }
        const activeOptionalColumns = this.props.userPreferencesContext.getActiveOptionalColumns(this.props.tableKey);
        const selectedColumns = _.compact(activeOptionalColumns.map(columnKey => _.find(optionalColumns, { value: columnKey})));
        const notSelectedColumns = optionalColumns.filter(col => !activeOptionalColumns.includes(col.value));
        return (
            <Dropdown overlayClassName={'box-shadow table-column-options'} trigger="click" placement="bottomRight" overlay={
                columnsReorder ? (
                    <MakeDNDMenu selectedColumnOrder={selectedColumns} notSelectedColumnOrder={notSelectedColumns} activeOptionalColumns={activeOptionalColumns} onDragEnd={this.onDragEnd} updateColumns={this.updateColumns} />
                ) : (
                <Menu className="p-l-xs">
                    {
                        optionalColumns.map(c => {
                            return (
                                <div className="m-b-xs" key={c.value}>
                                    <Checkbox 
                                        data-index={c.value}
                                        checked={activeOptionalColumns.indexOf(c.value) !== -1}
                                        onChange={this.updateColumns}
                                    >
                                        {c.text}
                                    </Checkbox>
                                </div>
                            )
                        }) 
                    }
                </Menu>
            )}>
                <Tooltip title="Additional columns" placement="topRight">
                    <SelectColumnIcon className="clickable m-r-xs" />
                </Tooltip>
            </Dropdown>
        )
    }
    handleSwitch(e) {
        const { dataSource = [] } = this.props;
        if (e) {
            this.setState({
                expandedValue: dataSource.map(data => isFunction(this.props.rowKey) ? this.props.rowKey(data) : data[this.props.rowKey] || data?.name)
            })
        }
        else {
            this.setState({
                expandedValue: []
            })
        }
    }

    onExpandRow(expanded, record) {
        let keys = cloneDeep(this.state.expandedValue);

        if(expanded) {
            keys.push(isFunction(this.props.rowKey) ? this.props.rowKey(record) : record[this.props.rowKey] || record?.name)
        } else {
            const key = isFunction(this.props.rowKey) ? this.props.rowKey(record) : record[this.props.rowKey] || record?.name;
            keys = remove(keys, (k) => k !== key);
        }
        this.setState({
            expandedValue: keys
        });
        if (this.props.onExpandRow) {
            this.props.onExpandRow(keys);
        }
    }

    onFilterBarChange(filters) {
        this.onChangeTable(undefined, filters, undefined);
    }

    saveFiltersInSession(filters) {
        const { storeSessionFilter, tableKey } = this.props;
        if (storeSessionFilter && tableKey) {
            setTemporarySessionStorage(tableKey, { filters });
        }
    }
    
    render(){
        const {page, filteredInfo, pageSize,sortedInfo } = this.state;
        const {dataSource = [], filterPadding, extras = false, messageBanner = null, loading = false, enableScroll, rowKey, title, showPagination = true, tableKey, className, showAdvanceFilter, paginationPosition, paginationAlignClassName, extraRowClassName = "", ...restProps} = this.props;
        const activeOptionalColumns = restProps.userPreferencesContext.getActiveOptionalColumns(tableKey);
        const extraFilter = [...restProps.extraFilter, ...restProps.columns]
        const filteredData = filterData(filteredInfo, (!dataSource ? [] : dataSource), extraFilter);
        // const filterBarStyle = filterPadding ? {} : {padding: 0};
        let hasFilter = false;
        const savedColumns = restProps.userPreferencesContext.getSavedColumnPreferences(tableKey, restProps.columns).filter(c => !c.optional || activeOptionalColumns.indexOf(getDataIndex(c)) !== -1).map((column) => {
            if(column.filterDropdown || column.sorter){
                hasFilter = true;
            }
            const childrenColumns = column.children
                ? map(column.children, (c) => ({
                    ...c,
                    filteredValue: filteredInfo[getDataIndex(c)] || null,
                }))
                : undefined;
            const _column = {
                ...column,
                filteredValue: filteredInfo[getDataIndex(column)] || null,
                sortOrder: sortedInfo.column === getDataIndex(column) && sortedInfo.order,
            };
            if(childrenColumns){
                _column.children = childrenColumns;
            }
            return _column;
        })
        const _columns = restProps.userPreferencesContext.getColumnOrder(tableKey, savedColumns, 'key');
        let tableScrollProp;
        if(enableScroll){
            if(_columns[0]){
                _columns[0].fixed = "left";
            }
            if(_columns.length !== 1 && _columns[_columns.length - 1]){
                _columns[_columns.length - 1].fixed = "right";
            }
            tableScrollProp = { x: MIN_SCREEN_WIDTH - FULL_MENU_WIDTH - BASE_MARGIN * 4 };
        }
        const columnOptions = this.renderColumnOptions();
        const reset = !_.isEmpty(this.state.sortedInfo) || !_.isEmpty(this.state.filteredInfo) || pageSize !== (this.props.defaultPageSize || 10) || this.props.resetEnable;

        return (
            <Row gutter={BASE_GUTTER}>
                {/* <Col sm={24}>
                    <Card bodyStyle={filterBarStyle} bordered={!_.isEmpty(filterPadding)} size={"small"}>
                        <Row gutter={BASE_MARGIN}> */}
                            {/* <Col sm={24} md={6}>
                                <Input.Search
                                    onChange={this.onSearch}
                                    placeholder="Search"
                                    value={searchText}
                                    suffix={searchText && <CloseCircleOutlined onClick={this.onSearch} />}
                                />
                            </Col> */}
                            <Col span={24}>
                                <div className="d-flex">
                                    <div className="flex-1">
                                        {
                                            title
                                            ?
                                            _.isString(title)
                                            ?
                                            <Typography.Title className="m-b-0" level={5}>{title}</Typography.Title>
                                            :
                                            title
                                            :
                                            null
                                        }
                                    </div>
                                    {messageBanner}
                                    <div>
                                        {this.props.expandable && dataSource.length && !this.props.hideExpandAll ? <>Expand All <Switch  size="small" checked={this.state.expandedValue.length === dataSource.length}  onClick={this.handleSwitch}/></> : null}
                                        {extras}
                                        {hasFilter && <Divider type="vertical" />}
                                        {hasFilter && 
                                            <Tooltip title="Reset Filter and Sorting" placement="topRight">
                                                <ResetFilterIcon onClick={reset ? this.resetFilters : null} className={ !reset ? 'disabled clickable' : "clickable"}/>
                                            </Tooltip>}
                                        {columnOptions && <Divider type="vertical" />}
                                        {columnOptions}
                                    </div>
                                </div>
                            </Col>
                            {/* <Col md={8} sm={24}>
                                {
                                    title
                                    &&
                                    <Typography.Title className="m-b-0" level={5}>{title}</Typography.Title>
                                }
                            </Col>
                            <Col md={16} sm={24} className="text-right">
                            </Col> */}
                            {showAdvanceFilter && <Col span={24}>
                            <FilterBar columns={[..._.uniqBy([..._columns, ...extraFilter], getDataIndex)]} onChange={this.onFilterBarChange} filters={filteredInfo} onClearFilter={this.resetFilters} />
                            </Col>}
                        {/* </Row>
                    </Card>
                </Col> */}
                <Col sm={24}>
                    <Table
                        {...restProps}
                        sticky={this.props.sticky ? { offsetHeader: 0 } : false}
                        className={classNames(className, 'table-container', { 'advance-filter': showAdvanceFilter && !restProps.showBothFilters })}
                        size={"small"}
                        columns={_columns}
                        dataSource={filteredData}
                        locale= {{...this.props.locale}}
                        rowKey={rowKey || (record => record.id)}
                        pagination={showPagination ? {
                            current: this.props.page || page,
                            size: this.props.paginationComponentSize || "default", 
                            pageSize, 
                            className: classNames("ant-table-pagination ant-table-pagination-right",  {"m-b-0": !paginationAlignClassName}, paginationAlignClassName),
                            showSizeChanger: this.props.showSizeChanger || false,
                            hideOnSinglePage: !this.props.showSizeChanger,
                            position: [paginationPosition],
                            pageSizeOptions: this.pageSizeOptions
                        } : false}
                        expandable={
                            restProps.expandable ? {
                                ...restProps.expandable,
                                expandedRowKeys: this.state.expandedValue,
                                // Show icons only if row is expandable
                                expandIcon:  ({ expanded, onExpand, record, expandable }) => {
                                    return expandable ? 
                                                expanded  ? (
                                                    <DownIcon onClick={e => onExpand(record, e)} className="clickable" size="lg" color="#8E8E8E"/>
                                                ) : (
                                                    <RightIcon onClick={e => onExpand(record, e)} className="clickable"  size="lg" color="#8E8E8E" />
                                                ) 
                                            : null
                                },
                                onExpand: this.onExpandRow
                            } : {}
                        }
                        onChange={this.onChangeTable}
                        loading={{spinning: loading, indicator: <LoadingIcon spin={false} />}}
                        scroll={tableScrollProp}
                        rowClassName={(record, index) =>
                            classNames(extraRowClassName, index % 2 !== 0 ? "table-row-even" : "table-row-odd")
                        }
                    />
                    {/* to avoid unwated margins when rendered inside another table */}
                    {restProps.patchForNestedTable ? <span /> : null}
                </Col>
            </Row>
        )
    }
}

const tableContainer = withUserPreferencesContext(withGlobalFilterContext(TableContainer));
export default withRouter(tableContainer);
export { tableContainer as TableContainer }

// const getExtraFiltersFromAllFilters = (filteredInfo, extraFilters) => {
//     let filtersToBeApplied;
//     _.forEach(filteredInfo, (value, key) => {
//         const columnFound = _.find(extraFilters, { 'dataIndex': key });
//         if (columnFound) {
//             if (_.isUndefined(filtersToBeApplied)) {
//                 filtersToBeApplied = {};
//             }
//             filtersToBeApplied[key] = value;
//         }
//     });

//     return filtersToBeApplied;
// }

export const filterData = (filteredInfo, dataSource, extraFilters) => {
    if (filteredInfo && !_.isEmpty(filteredInfo) && !_.isEmpty(extraFilters)) {
        return _.filter(dataSource, (entry) => {
            let matched = true;

            _.forEach(extraFilters, ({ dataIndex, onFilter }) => {
                const filterValueArr = filteredInfo[dataIndex];
                if (!_.isEmpty(filterValueArr) && _.isFunction(onFilter)) {
                    matched = !_.isUndefined(_.find(filterValueArr, (value) => onFilter(value, entry)));
                    if (!matched) {
                        return false;
                    }
                }
            });

            return matched;
        });
    }
    return dataSource || [];
};
